'use client';

import { useMemo, useEffect } from 'react';
import { useHits, useSearchBox } from 'react-instantsearch';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';

import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';

import { fCurrency } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { getCountryData } from 'src/assets/data';
import { promotionDataFormatter } from 'src/actions/product-ssr';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { SearchNotFound } from 'src/components/search-not-found';

import { useSearchProductStore } from './product-store';

// ----------------------------------------------------------------------

export function ProductSelect({ initialQuery }) {
  const router = useRouter();

  const { items } = useHits();
  const { query, refine } = useSearchBox();

  const { t, currentLang } = useTranslate();

  const { searchResults, setSearchResults } = useSearchProductStore();

  const results = useMemo(() => (items.length ? items.map(promotionDataFormatter) : []), [items]);

  const handleClick = (promotion) => {
    window.open(
      `${paths.product.details(promotion.objectID)}${newSearchParams({ country: promotion.country })}`,
      '_blank'
    );
  };

  const handleKeyUp = (event) => {
    if (query && event.key === 'Enter') {
      setSearchResults(results);
      router.push(
        `${paths.shop}${newSearchParams({ country: currentLang.countryName, query, list: 'search' })}`
      );
    }
  };

  useEffect(() => {
    if (query === initialQuery && results.length !== searchResults.length) {
      setSearchResults(results);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const renderOption = (props, product) => (
    <Box
      {...props}
      component="li"
      key={product?.objectID}
      onClick={() => handleClick(product)}
      sx={{ p: 1, gap: 1.5, width: 1, display: 'flex', cursor: 'pointer' }}
    >
      <Avatar
        variant="rounded"
        alt={product?.name}
        src={product?.coverUrl}
        sx={{ width: 50, height: 50, flexShrink: 0, borderRadius: 1 }}
      >
        <Iconify icon="f7:photo" width={35} />
      </Avatar>

      <Box sx={{ width: 1, display: 'grid', typography: 'subtitle2', gridTemplateRows: '2 1fr' }}>
        <div className="noWrap">{product?.name}</div>

        <div className="noWrap">
          {fCurrency(
            product?.priceSale,
            product?.numberFormat ?? getCountryData(product?.country)?.numberFormat
          )}
        </div>
      </Box>

      <Label variant="soft" startIcon={<Iconify icon="fluent-color:star-24" />}>
        {product?.totalRating}
      </Label>
    </Box>
  );

  const renderInput = (params) => (
    <TextField
      {...params}
      onKeyUp={handleKeyUp}
      placeholder={`${t('Search products, brands and more')}...`}
      slotProps={{
        input: {
          ...params.InputProps,
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <Iconify icon="eva:search-fill" sx={{ ml: 1, color: 'text.disabled' }} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: { xs: 1, md: 2.5 },
            height: { xs: 45, md: 'initial' },
            backgroundColor: 'background.paper',
          },
        },
      }}
    />
  );

  return (
    <Autocomplete
      fullWidth
      size="small"
      popupIcon={null}
      options={results}
      inputValue={query}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={() => query}
      noOptionsText={<SearchNotFound query={query} />}
      freeSolo={!!results.length || query === initialQuery}
      onInputChange={(event, newValue) => refine(newValue)}
      isOptionEqualToValue={(option, value) => option.objectID === value.objectID}
      slotProps={{
        popper: { placement: 'bottom-start' },
        paper: { sx: { [` .${autocompleteClasses.option}`]: { pl: 0.75 } } },
      }}
    />
  );
}
